import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/ScrollReveal';
import { isPromoActive } from '@/lib/promo';

interface CompactPromo {
  id: string;
  eyebrow: string;
  name: string;
  highlight: string;
  description: string;
  priceLine: string;
  bullets: string[];
}

const PROMOS: CompactPromo[] = [
  {
    id: 'molise-online',
    eyebrow: 'Attività turistiche · Molise & Abruzzo',
    name: 'Molise Online',
    highlight: 'A grande richiesta, Molise Online si espande anche in Abruzzo.',
    description:
      'Costruiamo un sito vetrina professionale per B&B, agriturismi, ristoranti e attività turistiche che ancora non hanno una presenza digitale strutturata: design curato, contenuti per il turismo, Google Maps e scheda Google Business Profile ottimizzata.',
    priceLine: 'Sconti fino al 30% · consegna in 7 giorni',
    bullets: [
      'Sito vetrina mobile-first, SEO-ready',
      'Google Maps + Google Business Profile',
      'Contenuti ottimizzati per il turismo',
    ],
  },
  {
    id: 'permuta-sito',
    eyebrow: 'Permuta il tuo vecchio sito',
    name: 'Permuta Sito',
    highlight: 'Applicabile esclusivamente ai siti vetrina di prima fascia.',
    description:
      'Se il tuo sito ha più di 3 anni lo rottamiamo e ne costruiamo uno nuovo — moderno, veloce, mobile-first e SEO-ready — con migrazione dei contenuti e consegna in 7 giorni.',
    priceLine: 'A partire da €790 · consegna in 7 giorni',
    bullets: [
      'Nuovo sito vetrina responsive e performante',
      'Migrazione contenuti dal sito esistente',
      'SEO on-page + Analytics/Search Console',
    ],
  },
];


const PromoSection = () => {
  if (!isPromoActive()) return null;

  return (
    <section
      id="promo"
      aria-labelledby="promo-heading"
      className="section-padding relative overflow-hidden bg-navy/30"
    >
      <div className="container-premium relative">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <ScrollReveal>
            <p className="text-gold tracking-[0.2em] uppercase text-sm mb-6">
              Offerta limitata · valida fino al 31 luglio
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 id="promo-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
              Due promo <span className="text-gradient-gold">siti vetrina</span>.
              <br className="hidden md:block" />
              Consegna in 7 giorni.
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-lg text-muted-foreground">
              Occasioni riservate a pochi. Scegli quella che fa per te.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {PROMOS.map((p, idx) => (
            <ScrollReveal key={p.id} delay={0.1 * (idx + 1)}>
              <Link
                to={`/promozioni#${p.id}`}
                aria-label={`Scopri la promo ${p.name}`}
                className="group block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-2xl"
              >
                <article className="glass-card-hover p-8 md:p-10 h-full flex flex-col">
                  {/* Eyebrow */}
                  <p className="text-gold tracking-[0.2em] uppercase text-xs mb-3">
                    {p.eyebrow}
                  </p>

                  {/* Titolo — stile serif del sito */}
                  <h3 className="font-serif text-2xl md:text-3xl mb-5 leading-tight">
                    {p.name}
                  </h3>

                  {/* Highlight box — elegante, coerente col design system */}
                  <div className="mb-5 min-h-[76px] flex">
                    <div className="w-full rounded-xl border border-gold/30 bg-gold/5 px-4 py-3 flex items-start">
                      <p className="font-serif text-base md:text-lg text-foreground/95 leading-snug">
                        {p.highlight}
                      </p>
                    </div>
                  </div>

                  {/* Descrizione */}
                  <p className="text-muted-foreground leading-relaxed mb-6 min-h-[96px]">
                    {p.description}
                  </p>


                  {/* Bullets */}
                  <ul className="space-y-3 mb-6">
                    {p.bullets.map((b) => (
                      <li key={b} className="pl-4 relative text-sm text-muted-foreground leading-relaxed">
                        <span className="absolute left-0 top-1.5 w-1 h-1 rounded-full bg-gold" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Riga economica */}
                  <div className="mt-auto pt-6 border-t border-border/60">
                    <p className="text-gold font-medium mb-4">{p.priceLine}</p>
                    <span className="inline-flex items-center text-gold font-medium tracking-wide">
                      Scopri la promo
                    </span>
                  </div>
                </article>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PromoSection;
